import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';

const TermsAndConditions = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-emerald-700 text-white py-8">
        <div className="max-w-5xl mx-auto px-4">
          <Link to="/">
            <Button
              variant="ghost"
              className="text-white hover:bg-emerald-600 mb-4"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Button>
          </Link>
          <h1 className="text-4xl font-bold">Terms & Conditions</h1>
          <p className="text-emerald-100 mt-2">Photo Booth Hire Agreement</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-12">
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">PHOTO BOOTH HIRE – TERMS & CONDITIONS</CardTitle>
          </CardHeader>
          <CardContent className="prose max-w-none space-y-6">
            <p className="text-gray-700">
              These Terms & Conditions ("Terms") apply to all bookings made with MySelfie Moments ("we", "us", "our"). 
              The customer ("you", "the Client") agrees to be bound by these Terms upon payment of any deposit or invoice.
            </p>

            <hr className="my-8" />

            {/* Section 1 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Booking & Acceptance of Terms</h2>
              <div className="space-y-3 text-gray-700">
                <p><strong>1.1</strong> Bookings are taken via email and confirmed by invoice.</p>
                <p><strong>1.2</strong> A booking is deemed confirmed once a deposit or part payment has been received.</p>
                <p><strong>1.3</strong> Payment of the deposit or any part payment constitutes full acceptance of these Terms & Conditions, whether or not the Client has signed or returned any separate documentation.</p>
                <p><strong>1.4</strong> It is the Client's responsibility to ensure that all event details supplied at the time of booking are accurate.</p>
              </div>
            </div>

            <hr className="my-8" />

            {/* Section 2 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Event Details & Client Responsibilities</h2>
              <div className="space-y-3 text-gray-700">
                <p><strong>2.1</strong> The Client is solely responsible for ensuring that:</p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>The event date, start time, finish time and full venue address provided are correct.</li>
                  <li>Adequate space is available for the Photo Booth, as advised prior to the event.</li>
                  <li>A suitable and uninterrupted power supply is available for the duration of the hire, unless otherwise agreed in writing.</li>
                  <li>A strong, stable and unrestricted Wi-Fi connection is provided where required for the selected service, including but not limited to instant sharing, email delivery, online galleries or cloud-based features.</li>
                  <li>The Photo Booth is positioned in a location where venue lighting, uplighters, spotlights, moving lights or colour-changing effects do not interfere with camera operation, image quality, printing or software performance.</li>
                  <li>Reasonable access is available for delivery, setup and breakdown, including parking where required.</li>
                </ul>
                <p className="mt-3"><strong>2.2</strong> Where Wi-Fi connectivity is unavailable, unstable or restricted, or where venue lighting adversely affects performance, we accept no responsibility for reduced functionality, delays, partial service delivery or image quality. The service shall be deemed delivered in full where the Photo Booth remains operational.</p>
                <p><strong>2.3</strong> We reserve the right to reposition the Photo Booth or suspend operation if venue conditions are unsuitable or unsafe.</p>
              </div>
            </div>

            <hr className="my-8" />

            {/* Section 3 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Hire Period & Overtime</h2>
              <div className="space-y-3 text-gray-700">
                <p><strong>3.1</strong> The hire period is as stated in the booking confirmation.</p>
                <p><strong>3.2</strong> Additional time may be provided at our discretion and charged at the agreed hourly rate, payable immediately.</p>
              </div>
            </div>

            <hr className="my-8" />

            {/* Section 4 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Payment Terms</h2>
              <div className="space-y-3 text-gray-700">
                <p><strong>4.1</strong> A non-refundable deposit is required to secure the booking.</p>
                <p><strong>4.2</strong> The remaining balance must be paid no later than 14 days prior to the event date, unless otherwise agreed in writing.</p>
                <p><strong>4.3</strong> Failure to pay the balance by the due date may result in cancellation without refund of any monies paid.</p>
              </div>
            </div>

            <hr className="my-8" />

            {/* Section 5 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Cancellations by the Client</h2>
              <div className="space-y-3 text-gray-700">
                <p><strong>5.1</strong> The deposit is non-refundable under all circumstances.</p>
                <p><strong>5.2</strong> If the Client cancels:</p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>More than 14 days before the event: the deposit is retained and no further charges apply.</li>
                  <li>14 days or less before the event: the full remaining balance is payable and non-refundable.</li>
                </ul>
                <p className="mt-3"><strong>5.3</strong> Cancellations must be made in writing. The cancellation date is the date written notice is received.</p>
              </div>
            </div>

            <hr className="my-8" />

            {/* Section 6 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Cancellation by Us</h2>
              <div className="space-y-3 text-gray-700">
                <p><strong>6.1</strong> In the unlikely event that we are unable to attend or provide the service, we will issue a full refund of all monies paid, including the deposit.</p>
                <p><strong>6.2</strong> Our liability is strictly limited to the refund of payments made. We shall not be liable for any additional costs, losses or damages incurred by the Client.</p>
              </div>
            </div>

            <hr className="my-8" />

            {/* Section 7 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Equipment Use & Damage</h2>
              <div className="space-y-3 text-gray-700">
                <p><strong>7.1</strong> The Client is responsible for the conduct of their guests and attendees during the hire period.</p>
                <p><strong>7.2</strong> Any loss, theft or damage to equipment (excluding fair wear and tear) caused by the Client or their guests shall be charged to the Client.</p>
                <p><strong>7.3</strong> We reserve the right to suspend service where equipment is at risk of damage. No refunds shall be issued in such circumstances.</p>
              </div>
            </div>

            <hr className="my-8" />

            {/* Section 8 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Liability & Insurance</h2>
              <div className="space-y-3 text-gray-700">
                <p><strong>8.1</strong> We hold Public Liability Insurance appropriate to the services we provide.</p>
                <p><strong>8.2</strong> Nothing in these Terms limits liability for death or personal injury caused by our negligence, fraud or fraudulent misrepresentation.</p>
                <p><strong>8.3</strong> Subject to the above, we shall not be liable for any indirect, consequential or economic loss, including loss of enjoyment, business loss or reputational damage.</p>
              </div>
            </div>

            <hr className="my-8" />

            {/* Section 9 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Corporate Bookings – Additional Terms</h2>
              <p className="text-gray-700 mb-3">Where the Client is acting in the course of business:</p>
              <div className="space-y-3 text-gray-700">
                <p><strong>9.1</strong> The Client confirms they have authority to enter into this agreement on behalf of the organisation.</p>
                <p><strong>9.2</strong> All schedules, venue details, access arrangements, IT/network permissions and technical requirements must be confirmed no later than 14 days prior to the event.</p>
                <p><strong>9.3</strong> Delays or limitations caused by venue access, security procedures, lighting conditions, Wi-Fi restrictions or IT policies shall not constitute a breach of contract by us.</p>
                <p><strong>9.4</strong> Our total liability for any claim arising from a corporate booking shall be strictly limited to the total fees paid.</p>
              </div>
            </div>

            <hr className="my-8" />

            {/* Section 10 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Force Majeure</h2>
              <div className="space-y-3 text-gray-700">
                <p><strong>10.1</strong> We shall not be liable for failure or delay in performance due to events beyond our reasonable control, including but not limited to severe weather, acts of God, government restrictions, venue closure or transport failure.</p>
                <p><strong>10.2</strong> Where possible, we will offer a reschedule or refund at our discretion.</p>
              </div>
            </div>

            <hr className="my-8" />

            {/* Section 11 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Images & Marketing</h2>
              <div className="space-y-3 text-gray-700">
                <p><strong>11.1</strong> Unless otherwise agreed in writing, we reserve the right to use images captured at events for promotional, marketing and portfolio purposes.</p>
                <p><strong>11.2</strong> Requests to opt out must be made in writing prior to the event.</p>
              </div>
            </div>

            <hr className="my-8" />

            {/* Section 12 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Data Protection & Third-Party Software</h2>
              <div className="space-y-3 text-gray-700">
                <p><strong>12.1</strong> We process personal data in accordance with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.</p>
                <p><strong>12.2</strong> As part of the Photo Booth service, we use third-party Photo Booth software provided by Photo Booth Supply Co. (Fiesta App), which is based in the United States.</p>
                <p><strong>12.3</strong> Images, videos and associated data captured during the event may be processed and temporarily stored on servers located outside the UK, including the United States, for the purpose of delivering the contracted service.</p>
                <p><strong>12.4</strong> By booking our services, the Client confirms they have obtained any necessary permissions or consents from event attendees for image capture, processing and international data transfer where applicable.</p>
                <p><strong>12.5</strong> We are not responsible for the data handling practices, security measures or service availability of third-party software providers.</p>
              </div>
            </div>

            <hr className="my-8" />

            {/* Section 13 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Governing Law</h2>
              <div className="space-y-3 text-gray-700">
                <p><strong>13.1</strong> These Terms & Conditions are governed by and construed in accordance with the laws of England and Wales.</p>
                <p><strong>13.2</strong> Any disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales.</p>
              </div>
            </div>

            <hr className="my-8" />

            <div className="bg-gray-50 p-6 rounded-lg mt-8">
              <p className="text-sm text-gray-600">
                <strong>Last Updated:</strong> November 2025
              </p>
              <p className="text-sm text-gray-600 mt-2">
                For any questions regarding these Terms & Conditions, please contact us via our website.
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="text-center">
          <Link to="/">
            <Button
              className="bg-emerald-700 hover:bg-emerald-800"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Website
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;
